import { URLS } from '@/lib/constants';

/**
 * Helpers for recognizing links into The Hang (the Circle community embedded
 * at openstudiojazz.com/community/). On native these open in the in-app
 * autologin WebView instead of Safari.
 */

function wpHostname(): string {
  try {
    return new URL(URLS.WORDPRESS_BASE).hostname.toLowerCase();
  } catch {
    return 'www.openstudiojazz.com';
  }
}

const COMMUNITY_HOSTS = new Set([
  wpHostname(),
  'www.openstudiojazz.com',
  'openstudiojazz.com',
]);

/** Matches the WP-side sanitize_community_next allowlist. */
const COMMUNITY_PATH_RE = /^\/community(\/[A-Za-z0-9\-_/]*)?$/;

export function isCommunityUrl(raw: string | null | undefined): boolean {
  if (!raw) return false;
  try {
    const u = new URL(raw);
    return COMMUNITY_HOSTS.has(u.hostname.toLowerCase()) && COMMUNITY_PATH_RE.test(u.pathname);
  } catch {
    return false;
  }
}

/**
 * Extract the community-relative path to land on after autologin.
 * Falls back to the community root for anything unexpected.
 */
export function communityNextPath(raw: string | null | undefined): string {
  if (raw) {
    try {
      const path = new URL(raw).pathname;
      if (COMMUNITY_PATH_RE.test(path)) return path;
    } catch {
      // fall through
    }
  }
  return '/community/';
}

/**
 * Events area: session detail pages (/os-sessions/...) and the calendar
 * (/events/...). This regex (plus the community one above) decides what may
 * load INSIDE the app WebView; the server-side autologin `next` guard is a
 * superset (any internal path) to support logged-in browser handoffs.
 */
const EVENTS_PATH_RE = /^\/(os-sessions|events)(\/[A-Za-z0-9\-_/]*)?$/;

export function isEventsUrl(raw: string | null | undefined): boolean {
  if (!raw) return false;
  try {
    const u = new URL(raw);
    return COMMUNITY_HOSTS.has(u.hostname.toLowerCase()) && EVENTS_PATH_RE.test(u.pathname);
  } catch {
    return false;
  }
}

/** Autologin-safe path for an events URL, or null when it isn't one. */
export function eventsNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const path = new URL(raw).pathname;
    return EVENTS_PATH_RE.test(path) ? path : null;
  } catch {
    return null;
  }
}
